import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Headline, Layout, Text } from '@oneaudi/unified-web-components';
import { useSelector } from 'react-redux';
import PaymentsOptions from '../VehicleOffersList/VehicleTile/PricingOptions/PaymentsOptions';
import {
  Wrapper,
  Title,
  TrimLineWrapper,
  DescriptionWrapper,
  ViewOfferCTA,
  InfoContainer,
  VehicleNameWrapper,
} from './SlideContent.styles';
import { CopyTwo } from '../Common/Common.styles';
import { COLORS } from '../../styles/variables';
import ArrowRight from '../../assets/icons/ArrowRight';
import { PromotionType } from '../../types/offers';
import formatModelName from '../../utils/formatModelName';
import removeYearFromModelName from '../../utils/removeYearFromModelName';
import { priceFormat } from '../../utils/formatPrice';
import { selectDealerContextId } from '../../reducers/dealerContextReducer';
import ButtonGroups from './ButtonGroups/ButtonGroups';
import Modal from '../Modal';
import useCtaUrls from '../../hooks/useCtaUrls';
import PaymentOptionsCa from '../VehicleOffersList/VehicleTileCa/PaymentOptionsCa';
import { selectZipCode } from '../../reducers/zipCodeReducer';

export interface SlideContentProps {
  isActive?: boolean;
  offer: PromotionType;
  confirmedZipcode: string;
}

const SlideContent: React.FC<SlideContentProps> = ({ offer, isActive, confirmedZipcode }) => {
  const { t } = useTranslation();

  const {
    offerType,
    programToDate,
    customerDownPayment,
    monthlyPayment,
    programName,
    term,
    aprRate,
    offerModelDetail,
    programShortDescription,
  } = offer;

  const { modelYear, trimline, modelCodeDescription, modelName, dag } = offerModelDetail;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { market } = useSelector(selectZipCode);

  const isCaMarket = market === 'CA';

  const inventoryUrl = useCtaUrls(offer);

  const isBonusOffers = offerType === 'credit';

  const { dealerId } = useSelector(selectDealerContextId);
  const trimmedModelName = isCaMarket
    ? modelName
    : removeYearFromModelName(modelYear, modelCodeDescription);
  const msrp = priceFormat(offer?.offerModelDetail?.msrp);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const expiryDate = `${t('promo.tile.offerEnd')} ${programToDate}`;

  const offerLabel = useMemo(() => {
    if (isCaMarket) return null;
    // if it's a stack offer use the program name of stack offer otherwise use program name from actual offer
    if (programName === 'AUD_SOA_COMBINED') return 'Season of Audi Bonus + Audi Credit';
    if (programName === 'AUD_SALES_EVENT') return 'Season of Audi Customer Bonus';
    if (programName === 'AUD_CUSTOMER_CREDIT') return 'Customer Bonus';
    if (programName === 'AUD_COSTCO_SELECT_PROGRAM') return 'Costco Select Bonus';
    return 'Audi Credit';
  }, [programName, isCaMarket]);

  const modelNameToRender = isCaMarket ? modelName : formatModelName(modelCodeDescription);

  const VehicleName = (
    <VehicleNameWrapper>
      <Headline
        data-testid="featured-offer-card-title"
        tag="h4"
        variant={{
          order: '4',
          type: 'headline',
          weight: 'bold',
        }}
      >
        {modelNameToRender}
      </Headline>
    </VehicleNameWrapper>
  );

  const TrimLineName = useMemo(() => {
    if (offer.offerType === 'credit') {
      return (
        <Title data-testid="featured-offer-card-bonus">
          {priceFormat(offer.creditBonus)} {offerLabel}
        </Title>
      );
    }
    if (offer.offerType === 'lease' && trimline.toLocaleLowerCase() !== 'base') {
      return (
        <Text
          data-testid="featured-offer-card-trimline"
          variant={{
            order: '2',
            style: 'normal',
          }}
        >
          {trimline}
        </Text>
      );
    }
    return null;
  }, [offer.creditBonus, offer.offerType, offerLabel, trimline]);

  const Description = <CopyTwo color={COLORS.grey60}>{programShortDescription}</CopyTwo>;

  const ShowDetailsCTA = useMemo(() => {
    return (
      <ViewOfferCTA
        data-testid={isBonusOffers ? 'slide-cta-bonus-details' : 'slide-cta-offer-details'}
        onClick={() => setIsModalOpen(true)}
        tabIndex={isActive ? 0 : -1}
        aria-label={`view offer details ${offerType ?? ''}`}
      >
        <div>
          {isBonusOffers ? t('promo.offers.viewBonusOfferCta') : t('promo.offers.viewOffer')}
        </div>
        <ArrowRight color={COLORS.grey60} />
      </ViewOfferCTA>
    );
  }, [isActive, isBonusOffers, offerType, t]);

  return (
    <Wrapper
      data-promotion-type={offerType} // either lease, finane, or credit
      data-promotion-offerdate={programToDate} // validity date of offer
      data-promotion-credit={offerType === 'credit' ? offer.creditBonus : 'null'} // credit bonus offer
      data-promotion-lease-downpayment={customerDownPayment?.toString() ?? 'null'} // downpayment for lease offer
      data-promotion-lease-payment={monthlyPayment?.toString() ?? 'null'} // monthly payment for lease offer
      data-promotion-lease-termlength={offerType === 'lease' ? term : 'null'} // term length for lease offer
      data-promotion-finance-apr={offerType === 'finance' ? aprRate : 'null'} // apr for finance offer
      data-promotion-finance-termlength={offerType === 'finance' ? term : 'null'} // termlength for finance offer
      data-vehicle-model-name={trimmedModelName} // name of model -excludes year // ??
      data-vehicle-model-year={modelYear} // year of model
      data-vehicle-price={msrp !== '-' ? msrp : 'null'} // price of vehicle ??
      data-vehicle-trim={trimline} // vehicle trim
      data-testid={`featured-offer-tile-${dag}`}
      isCaMarket={isCaMarket}
    >
      <InfoContainer>
        {VehicleName}
        <TrimLineWrapper>{TrimLineName}</TrimLineWrapper>
        <div>
          {isCaMarket ? (
            <PaymentOptionsCa
              offer={offer}
              isFeatured
              showOfferDetails={() => setIsModalOpen(true)}
            />
          ) : (
            <PaymentsOptions offer={offer} labelColor={COLORS.grey60} hasSmallFont />
          )}
        </div>
        {!isCaMarket && (
          <>
            <DescriptionWrapper>{Description}</DescriptionWrapper>
            <CopyTwo color={COLORS.grey60}>{expiryDate}</CopyTwo>
            <Layout>{ShowDetailsCTA}</Layout>
          </>
        )}
      </InfoContainer>
      {/* Render Contact Dealer CTA and View Inventory CTA on Featured Offer Tiles */}
      <ButtonGroups
        offer={offer}
        confirmedZipcode={confirmedZipcode}
        inventoryUrl={inventoryUrl}
        dealerId={dealerId}
      />
      {isModalOpen && (
        <Modal
          offer={offer}
          inventoryUrl={inventoryUrl}
          onCloseModal={handleCloseModal}
          dealerId={dealerId}
          isModal
        />
      )}
    </Wrapper>
  );
};

export default SlideContent;
